// ABOUTME: Row-grouping popover for ShDataGrid3 — pick which column to group by, plus a granularity if it's a date column.
// ABOUTME: Built with the ActionButton/ActionIcon pattern from MTopbarAction. Each row shows a preview inline.

import { Box, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";

import BlockOutlinedIcon from "@mui/icons-material/BlockOutlined";
import WorkspacesOutlinedIcon from "@mui/icons-material/WorkspacesOutlined";
import CheckIcon from "@mui/icons-material/Check";

import { tokens } from "../../../theme";
import PopoverModal from "../Modal/PopoverModal";
import Text from "../text/Text";
import { ActionButton } from "../../global/Modals/ActionModal/components/ActionButton";
import { ActionIcon } from "../../global/Modals/ActionModal/components/ActionIcon";
import { DropdownBase } from "../dropdowns/Dropdown";

import { useGridFilter } from "../../../contexts/GridFilterContext";
import { updateGroupBy } from "../../../newapi/global/filter/gridFilterSlice";
import { resolveDateGranularities } from "./groupingDateGranularities";

/**
 * GridGrouping3
 *
 * Its own popover (NOT part of GridcolumnSettings3). Lists every code column
 * that declared a `grouping` block at the call site as an ActionButton row,
 * plus a "None" row at the top. Each column row shows the call-site's
 * `grouping.preview` JSX as the trailing element, so you see every column's
 * styled preview side-by-side before picking one.
 *
 * When the active column declares `grouping.granularities`, a second stack of
 * ActionButton rows appears below listing the granularity options. Each
 * granularity row shows a live sample rendered via the built-in `formatKey`
 * helper from groupingDateGranularities.js — e.g. "Hour" renders "14:00" as
 * its preview while "Day" renders "April 11, 2024".
 *
 * All writes go to gridFilterSlice.viewInstances[index][id].groupBy via
 * `updateGroupBy`; useGridLayout picks that up when saving / loading saved
 * configs.
 *
 * Props:
 *   ...popover props (id, open, anchorEl, onClose)
 *   columns — the call-site's code columns array (same one passed to ShDataGrid3)
 */
const GridGrouping3View = ({ columns }) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const dispatch = useDispatch();
    const { index, id } = useGridFilter();

    const runtimeGroupBy = useSelector(
        (state) => state.gridFilter?.viewInstances?.[index]?.[id]?.groupBy ?? null
    );

    const groupableColumns = Array.isArray(columns)
        ? columns.filter((c) => c?.grouping)
        : [];

    const selectGroupColumn = (field) => {
        if (!field) {
            dispatch(updateGroupBy({ index, id, groupBy: null }));
            return;
        }
        const col = groupableColumns.find((c) => c.field === field);
        if (!col) return;
        const firstGranularity =
            Array.isArray(col.grouping.granularities) && col.grouping.granularities.length > 0
                ? resolveDateGranularities(col.grouping.granularities)[0]?.id ?? null
                : null;
        dispatch(
            updateGroupBy({
                index,
                id,
                groupBy: { field, granularity: firstGranularity },
            })
        );
    };

    const selectGranularity = (granularityId) => {
        if (!runtimeGroupBy?.field) return;
        dispatch(
            updateGroupBy({
                index,
                id,
                groupBy: { field: runtimeGroupBy.field, granularity: granularityId },
            })
        );
    };

    const isStandAlone = window.matchMedia("(display-mode: standalone)").matches;

    const checkmark = (
        <CheckIcon
            fontSize="small"
            sx={{
                color:
                    theme.palette.mode === "dark"
                        ? colors.blue[400]
                        : colors.primary[400],
            }}
        />
    );

    return (
        <Box
            sx={{
                background: theme.palette.mode === "dark" ? colors.grey[100] : "#fff",
                padding: 1.5,
                paddingBottom: isStandAlone ? 4 : 1.5,
                display: "flex",
                gap: 1,
                flexDirection: "column",
                borderRadius: "24px",
                borderBottomLeftRadius: 0,
                borderBottomRightRadius: 0,
                "@media screen and (max-width: 48em)": {
                    borderRadius: "32px",
                    borderBottomLeftRadius: 0,
                    borderBottomRightRadius: 0,
                },
            }}
        >
            <ActionButton onClick={() => selectGroupColumn(null)}>
                <ActionIcon>
                    <BlockOutlinedIcon fontSize="small" />
                </ActionIcon>
                <Text medium>{t("none")}</Text>
                {!runtimeGroupBy?.field && (
                    <Box sx={{ marginLeft: "auto", display: "flex", alignItems: "center" }}>
                        {checkmark}
                    </Box>
                )}
            </ActionButton>

            {groupableColumns.map((col) => {
                const isActive = runtimeGroupBy?.field === col.field;
                const colGranularities =
                    Array.isArray(col.grouping?.granularities) && col.grouping.granularities.length > 0
                        ? resolveDateGranularities(col.grouping.granularities)
                        : [];
                const hasGranularities = colGranularities.length > 0;

                // Resolve the current granularity id (and its index) for the
                // embedded DropdownBase on date-type columns.
                const currentGranId = isActive
                    ? runtimeGroupBy?.granularity ?? colGranularities[0]?.id
                    : colGranularities[0]?.id;
                const currentGranIdx = Math.max(
                    0,
                    colGranularities.findIndex((g) => g.id === currentGranId)
                );
                const currentGran = colGranularities[currentGranIdx];

                return (
                    <ActionButton
                        key={col.field}
                        onClick={() => selectGroupColumn(col.field)}
                        sx={
                            isActive
                                ? {
                                      background:
                                          theme.palette.mode === "dark"
                                              ? colors.blue[100]
                                              : colors.primary[100],
                                    
                                  }
                                : undefined
                        }
                    >
                        <ActionIcon>
                            <WorkspacesOutlinedIcon fontSize="small" />
                        </ActionIcon>
                        <Text sx={{mr:3,}} medium>{t(col.headerName || col.field)}</Text>
                        <Box
                            sx={{
                                marginLeft: "auto",
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                            }}
                        >
                            {hasGranularities ? (
                                // Inline granularity picker — DropdownBase child stops
                                // propagation so clicking it doesn't re-fire the column
                                // row's select handler.
                                <Box
                                    onClick={(e) => e.stopPropagation()}
                                    onMouseDown={(e) => e.stopPropagation()}
                                >
                                    <DropdownBase
                                        key={currentGranId}
                                        options={colGranularities.map((g) => t(g.label))}
                                        onChange={(selectedIdx) => {
                                            const chosen = colGranularities[selectedIdx];
                                            if (!chosen) return;
                                            if (isActive) {
                                                selectGranularity(chosen.id);
                                            } else {
                                                // Clicking a granularity on a non-active
                                                // date column implicitly activates that
                                                // column and sets the chosen granularity.
                                                dispatch(
                                                    updateGroupBy({
                                                        index,
                                                        id,
                                                        groupBy: { field: col.field, granularity: chosen.id },
                                                    })
                                                );
                                            }
                                        }}
                                        selected={currentGranIdx}
                                        noSelection={t("no_label")}
                                        closeOnSelection
                                        disallowDeselection
                                        transformOrigin={{ vertical: "top" }}
                                        anchorOrigin={{ vertical: "bottom" }}
                                    >
                                        <Box
                                            className="active"
                                            sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                height: 32,
                                                borderRadius: "8px",
                                                px: 1.25,
                                                cursor: "pointer",
                                                userSelect: "none",
                                                background:
                                                    theme.palette.mode === "dark"
                                                        ? colors.grey[200]
                                                        : colors.grey[100],
                                                "&:hover": {
                                                    transition: "250ms",
                                                    background:
                                                        theme.palette.mode === "dark"
                                                            ? colors.grey[300]
                                                            : colors.bg.tertiary,
                                                },
                                            }}
                                        >
                                            <Text
                                                medium
                                                sx={{ fontSize: 12, color: colors.txt.primary }}
                                            >
                                                {currentGran ? t(currentGran.label) : null}
                                            </Text>
                                        </Box>
                                    </DropdownBase>
                                </Box>
                            ) : (
                                col.grouping?.preview
                            )}
                            {/* {isActive && checkmark} */}
                        </Box>
                    </ActionButton>
                );
            })}
        </Box>
    );
};

const GridGrouping3 = (props) => {
    const { columns, ...popoverProps } = props;
    return (
        <PopoverModal {...popoverProps}>
            <GridGrouping3View columns={columns} />
        </PopoverModal>
    );
};

export default GridGrouping3;
